import API_BASE from "../../utils/api";
import React, { useState, useEffect } from "react";
import axios from "axios";
import Sidebar from "../../components/Sidebar";
import ProfileDropdown from "../../components/ProfileDropdown";
import Modal from "../../components/Modal";
import ConfirmDialog from "../../components/ConfirmDialog";

const DISCOUNT_TYPES = [
  { value: "scholarship", label: "Scholarship" },
  { value: "staff_ward", label: "Staff Ward" },
  { value: "rte", label: "RTE" },
  { value: "custom", label: "Custom" },
];

const emptyForm = () => ({ name: "", type: "custom", calcMethod: "percent", value: "", capAmount: "" });

const formatValue = (d) => (d.calcMethod === "percent" ? `${d.value}%` : `Rs. ${d.value}`);

const DiscountDefinitions = () => {
  const [definitions, setDefinitions] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showModal, setShowModal] = useState(false);
  const [editing, setEditing] = useState(null);
  const [form, setForm] = useState(emptyForm());
  const [error, setError] = useState("");
  const [deleteTarget, setDeleteTarget] = useState(null);
  const [status, setStatus] = useState({ type: "", message: "" });

  const fetchDefinitions = async () => {
    setLoading(true);
    try {
      const res = await axios.get(`${API_BASE}/discount-definitions`);
      setDefinitions(res.data);
    } catch (_) {
      setStatus({ type: "error", message: "Failed to load discounts" });
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { fetchDefinitions(); }, []);

  useEffect(() => {
    if (status.message) {
      const t = setTimeout(() => setStatus({ type: "", message: "" }), 3000);
      return () => clearTimeout(t);
    }
  }, [status]);

  const openAdd = () => { setEditing(null); setForm(emptyForm()); setError(""); setShowModal(true); };
  const openEdit = (d) => {
    setEditing(d);
    setForm({ name: d.name, type: d.type, calcMethod: d.calcMethod, value: d.value, capAmount: d.capAmount || "" });
    setError("");
    setShowModal(true);
  };

  const handleSave = async () => {
    setError("");
    if (!form.name.trim()) return setError("Name is required");
    if (form.value === "" || Number(form.value) < 0) return setError("A valid value is required");

    const payload = {
      name: form.name,
      type: form.type,
      calcMethod: form.calcMethod,
      value: Number(form.value),
      capAmount: Number(form.capAmount) || 0,
    };
    try {
      if (editing) {
        await axios.put(`${API_BASE}/discount-definitions/${editing._id}`, payload);
        setStatus({ type: "success", message: "Discount updated" });
      } else {
        await axios.post(`${API_BASE}/discount-definitions`, payload);
        setStatus({ type: "success", message: "Discount created" });
      }
      setShowModal(false);
      fetchDefinitions();
    } catch (err) {
      setError(err.response?.data?.error || "Failed to save discount");
    }
  };

  const toggleActive = async (d) => {
    try {
      await axios.put(`${API_BASE}/discount-definitions/${d._id}`, { isActive: !d.isActive });
      fetchDefinitions();
    } catch (_) {
      setStatus({ type: "error", message: "Failed to update discount" });
    }
  };

  const handleDelete = async () => {
    if (!deleteTarget) return;
    try {
      await axios.delete(`${API_BASE}/discount-definitions/${deleteTarget._id}`);
      setStatus({ type: "success", message: "Discount deleted" });
      setDeleteTarget(null);
      fetchDefinitions();
    } catch (_) {
      setStatus({ type: "error", message: "Failed to delete discount" });
      setDeleteTarget(null);
    }
  };

  return (
    <div className="flex h-screen bg-gray-100">
      <Sidebar />
      <div className="flex-1 overflow-auto">
        <nav className="bg-white shadow-sm sticky top-0 z-10">
          <div className="px-6 py-4 flex justify-between items-center">
            <h1 className="text-2xl font-semibold text-gray-800">Discount Definitions</h1>
            <ProfileDropdown />
          </div>
        </nav>

        {status.message && (
          <div className={`fixed top-4 right-4 z-[100] max-w-sm p-4 rounded-lg shadow-lg ${status.type === "success" ? "bg-green-100 text-green-700 border border-green-400" : "bg-red-100 text-red-700 border border-red-400"}`}>
            {status.message}
          </div>
        )}

        <div className="p-6">
          <div className="bg-white rounded-lg shadow p-6 mb-6 flex justify-between items-center flex-wrap gap-3">
            <div>
              <h2 className="text-lg font-semibold">Scholarship / Staff Ward / RTE / Custom Discounts</h2>
              <p className="text-sm text-gray-500">
                Define a discount once, then assign it to students from their profile. It auto-applies every time fees are generated.
                (Sibling discounts are configured separately, in Settings → Fee Settings.)
              </p>
            </div>
            <button onClick={openAdd} className="bg-indigo-600 text-white px-4 py-2 rounded hover:bg-indigo-700 transition flex items-center">
              <span className="mr-2">+</span> Add Discount
            </button>
          </div>

          <div className="bg-white rounded-lg shadow overflow-hidden">
            <div className="overflow-x-auto">
              <table className="min-w-full divide-y divide-gray-200">
                <thead className="bg-gray-50">
                  <tr>
                    <th className="px-5 py-3 text-left text-xs font-semibold text-gray-500 uppercase tracking-wider">Name</th>
                    <th className="px-5 py-3 text-left text-xs font-semibold text-gray-500 uppercase tracking-wider">Type</th>
                    <th className="px-5 py-3 text-left text-xs font-semibold text-gray-500 uppercase tracking-wider">Value</th>
                    <th className="px-5 py-3 text-left text-xs font-semibold text-gray-500 uppercase tracking-wider">Cap</th>
                    <th className="px-5 py-3 text-left text-xs font-semibold text-gray-500 uppercase tracking-wider">Status</th>
                    <th className="px-5 py-3 text-center text-xs font-semibold text-gray-500 uppercase tracking-wider">Actions</th>
                  </tr>
                </thead>
                <tbody className="bg-white divide-y divide-gray-200">
                  {loading ? (
                    <tr><td colSpan="6" className="px-6 py-8 text-center text-gray-500">Loading…</td></tr>
                  ) : definitions.length === 0 ? (
                    <tr><td colSpan="6" className="px-6 py-12 text-center text-gray-500">
                      <div className="text-4xl mb-2">🎓</div>
                      <p>No discounts defined yet</p>
                      <button onClick={openAdd} className="mt-3 text-indigo-600 hover:text-indigo-800 text-sm">Add your first discount</button>
                    </td></tr>
                  ) : (
                    definitions.map((d) => (
                      <tr key={d._id} className="hover:bg-gray-50">
                        <td className="px-5 py-4 font-medium text-gray-800">{d.name}</td>
                        <td className="px-5 py-4 text-sm text-gray-600">{DISCOUNT_TYPES.find((t) => t.value === d.type)?.label || d.type}</td>
                        <td className="px-5 py-4 text-sm text-gray-600">{formatValue(d)}</td>
                        <td className="px-5 py-4 text-sm text-gray-600">{d.capAmount > 0 ? `Rs. ${d.capAmount}` : "—"}</td>
                        <td className="px-5 py-4">
                          <button
                            onClick={() => toggleActive(d)}
                            className={`px-2 py-0.5 rounded-full text-xs font-medium ${d.isActive ? "bg-green-100 text-green-700" : "bg-gray-100 text-gray-500"}`}
                          >
                            {d.isActive ? "Active" : "Inactive"}
                          </button>
                        </td>
                        <td className="px-5 py-4 text-center">
                          <button onClick={() => openEdit(d)} className="text-amber-600 hover:underline text-sm mr-3">Edit</button>
                          <button onClick={() => setDeleteTarget(d)} className="text-red-600 hover:underline text-sm">Delete</button>
                        </td>
                      </tr>
                    ))
                  )}
                </tbody>
              </table>
            </div>
          </div>
        </div>
      </div>

      {showModal && (
        <Modal
          title={editing ? "Edit Discount" : "Add Discount"}
          onClose={() => setShowModal(false)}
          footer={
            <>
              <button onClick={() => setShowModal(false)} className="px-4 py-2 border rounded-md text-gray-600 hover:bg-gray-100">Cancel</button>
              <button onClick={handleSave} className="px-4 py-2 bg-indigo-600 text-white rounded-md hover:bg-indigo-700">Save</button>
            </>
          }
        >
          <div className="space-y-4">
            {error && <div className="p-3 bg-red-50 border border-red-200 text-red-700 rounded-lg text-sm">{error}</div>}
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Name *</label>
              <input type="text" value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} placeholder="e.g. Merit Scholarship" className="w-full border rounded-md px-3 py-2" />
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Type</label>
              <select value={form.type} onChange={(e) => setForm({ ...form, type: e.target.value })} className="w-full border rounded-md px-3 py-2">
                {DISCOUNT_TYPES.map((t) => <option key={t.value} value={t.value}>{t.label}</option>)}
              </select>
            </div>
            <div className="grid grid-cols-2 gap-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Calculation</label>
                <select value={form.calcMethod} onChange={(e) => setForm({ ...form, calcMethod: e.target.value })} className="w-full border rounded-md px-3 py-2">
                  <option value="percent">Percent (%)</option>
                  <option value="flat">Flat (Rs.)</option>
                </select>
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Value *</label>
                <input type="number" min="0" value={form.value} onChange={(e) => setForm({ ...form, value: e.target.value })} className="w-full border rounded-md px-3 py-2" />
              </div>
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Cap Amount (Rs., optional)</label>
              <input type="number" min="0" value={form.capAmount} onChange={(e) => setForm({ ...form, capAmount: e.target.value })} placeholder="0 = uncapped" className="w-full border rounded-md px-3 py-2" />
            </div>
          </div>
        </Modal>
      )}

      {deleteTarget && (
        <ConfirmDialog
          title="Delete Discount"
          message={`Delete "${deleteTarget.name}"? Students it's currently assigned to will lose this discount on future fee generation.`}
          confirmLabel="Delete"
          danger
          onConfirm={handleDelete}
          onCancel={() => setDeleteTarget(null)}
        />
      )}
    </div>
  );
};

export default DiscountDefinitions;
